import { Link } from '@tanstack/react-router';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { RunStatusBadge, runStatusLabel } from '@/components/ui/RunStatusBadge';
import type { RolloutCustomerResult, RolloutResponse } from '@/hooks/useRollout';

type RolloutRun = RolloutCustomerResult['runs'][number];

const ROLLOUT_RESULT_COLUMNS: ReadonlyArray<Column<RolloutRun>> = [
  {
    id: 'routine',
    header: 'Routine',
    cell: (run) => <code className="font-mono text-xs">{run.routine_slug}</code>,
    filterValue: (run) => run.routine_slug,
  },
  {
    id: 'mode',
    header: 'Mode',
    filter: 'select',
    filterValue: (run) => (run.mode === 'check' ? 'Dry-Run' : run.mode),
    cell: (run) => (
      <Badge tone={run.mode === 'check' ? 'neutral' : 'danger'}>
        {run.mode === 'check' ? 'Dry-Run' : run.mode}
      </Badge>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    filter: 'select',
    filterValue: (run) => runStatusLabel(run.status),
    cell: (run) => <RunStatusBadge status={run.status} />,
  },
  {
    id: 'aktion',
    header: ' ',
    filter: false,
    cell: (run) => (
      <Link
        to="/runs/$id"
        params={{ id: run.run_id }}
        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        Lauf öffnen
        <ExternalLink className="size-3" aria-hidden="true" />
      </Link>
    ),
  },
];

/**
 * Ergebnis der Ausrollung: je Kunde die erzeugten Läufe (mit Link) und
 * Teilfehler. Übersprungene Kunden bleiben sichtbar, aber grau - sonst
 * fehlt in der Nachkontrolle, warum ein Kunde nicht dran war.
 */

interface RolloutResultProps {
  result: RolloutResponse;
}

function byPosition(a: RolloutCustomerResult, b: RolloutCustomerResult): number {
  return a.position - b.position;
}

export function RolloutResult({ result }: RolloutResultProps): JSX.Element {
  const customers = [...result.customers].sort(byPosition);
  const { totals } = result;
  const errorCount = customers.reduce((n, c) => n + c.errors.length, 0);

  return (
    <Card
      title="Ergebnis der Ausrollung"
      description={`${totals.runs} ${totals.runs === 1 ? 'Lauf' : 'Läufe'} bei ${totals.customers_with_runs} von ${totals.customers} Kunden.`}
    >
      <div data-testid="rollout-result">
        <p className="mb-3 text-sm" data-testid="rollout-result-totals">
          <strong>{totals.runs}</strong> gestartet
          {errorCount > 0 && (
            <>
              , <strong className="text-danger">{errorCount}</strong> Fehler
            </>
          )}
          .
        </p>

        <ul className="flex flex-col gap-3">
          {customers.map((customer) => (
            <li
              key={customer.customer_id}
              className={[
                'rounded-md border border-border p-3',
                customer.skipped_reason ? 'bg-surface-2/40 text-muted' : '',
              ].join(' ')}
              data-testid={`rollout-result-customer-${customer.slug}`}
            >
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border bg-surface-2 text-xs font-semibold tabular-nums text-text">
                  {customer.position}
                </span>
                <span className="font-medium text-text">{customer.name}</span>
                <code className="font-mono text-xs">{customer.slug}</code>
                {customer.skipped_reason && <Badge tone="neutral">uebersprungen</Badge>}
              </div>

              {customer.skipped_reason && (
                <p className="text-sm">{customer.skipped_reason}</p>
              )}

              {customer.runs.length > 0 && (
                <DataTable
                  caption={`Läufe ${customer.name}`}
                  columns={ROLLOUT_RESULT_COLUMNS}
                  rows={customer.runs}
                  rowKey={(run) => run.run_id}
                  filters={false}
                  rowTestId={(run) => `rollout-result-run-${run.run_id}`}
                />
              )}

              {customer.errors.length > 0 && (
                <ul
                  className="mt-2 rounded-md border border-danger/40 bg-danger/5 p-2 text-sm text-danger"
                  role="alert"
                >
                  {customer.errors.map((err) => (
                    <li
                      key={`${err.routine_slug}:${err.message}`}
                      className="flex items-start gap-2"
                    >
                      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                      <span>
                        <code className="font-mono text-xs">{err.routine_slug}</code>{' '}
                        {err.message}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>

        <footer className="mt-4">
          <Link to="/runs">
            <Button variant="secondary">Run-Liste öffnen</Button>
          </Link>
        </footer>
      </div>
    </Card>
  );
}
